/**
 * Chart widget wrapper — bar / line / donut (P3-S2 Doc 42).
 */
import type { WidgetSpec } from "@/api/dashboards";
import { BarChart, Donut, LineChart } from "@/components/ui/svg-charts";

interface Props {
  widget: WidgetSpec;
  data: Record<string, unknown> | null;
  loading: boolean;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function asNumber(v: unknown): number {
  if (typeof v === "number") return v;
  if (v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
}

function formatVal(v: number, format: string | undefined): string {
  if (format === "usd") return `$${v.toFixed(v < 1 ? 3 : 2)}`;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toLocaleString("vi-VN");
}

export function ChartWidget({ widget, data, loading }: Props): JSX.Element {
  if (loading) {
    return (
      <div className="h-full">
        <div className="text-xs font-semibold mb-2">{widget.title}</div>
        <div className="bg-muted animate-pulse rounded h-32" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="h-full flex flex-col">
        <div className="text-xs font-semibold mb-1">{widget.title}</div>
        <div className="text-xs text-muted-foreground italic flex-1 flex items-center justify-center">
          (no data)
        </div>
      </div>
    );
  }

  const opts = widget.options;
  const items = asArray(data.items ?? data.points);

  if (widget.type === "bar") {
    const xField = String(opts.x_field ?? "label");
    const yField = String(opts.y_field ?? "value");
    const limitOpt = opts.limit as number | undefined;
    const sliced = limitOpt ? items.slice(0, limitOpt) : items.slice(0, 30);
    const numbers = sliced.map((it) =>
      asNumber((it as Record<string, unknown>)[yField]),
    );
    const labels = sliced.map((it) =>
      String((it as Record<string, unknown>)[xField] ?? ""),
    );
    const format = opts.format as string | undefined;
    return (
      <ChartFrame title={widget.title} description={widget.description}>
        {numbers.length > 0 ? (
          <BarChart
            data={numbers}
            labels={labels}
            formatValue={(v) => formatVal(v, format)}
            height={140}
          />
        ) : (
          <NoData />
        )}
      </ChartFrame>
    );
  }

  if (widget.type === "line") {
    const xField = String(opts.x_field ?? "date");
    const yField = String(opts.y_field ?? "value");
    const points = items.map((it) => ({
      x: String((it as Record<string, unknown>)[xField] ?? ""),
      y: asNumber((it as Record<string, unknown>)[yField]),
    }));
    const format = opts.format as string | undefined;
    return (
      <ChartFrame title={widget.title} description={widget.description}>
        {points.length > 0 ? (
          <LineChart
            data={points}
            formatValue={(v) => formatVal(v, format)}
            height={150}
          />
        ) : (
          <NoData />
        )}
      </ChartFrame>
    );
  }

  if (widget.type === "donut") {
    const labelField = String(opts.label_field ?? "label");
    const valueField = String(opts.value_field ?? "value");
    const segments = items.map((it) => ({
      label: String((it as Record<string, unknown>)[labelField] ?? "?"),
      value: asNumber((it as Record<string, unknown>)[valueField]),
    }));
    return (
      <ChartFrame title={widget.title} description={widget.description}>
        {segments.length > 0 ? (
          <Donut data={segments} size={130} />
        ) : (
          <NoData />
        )}
      </ChartFrame>
    );
  }

  return <NoData />;
}

function ChartFrame({
  title,
  description,
  children,
}: {
  title: string;
  description?: string | null;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="h-full flex flex-col">
      <div className="text-xs font-semibold mb-0.5">{title}</div>
      {description ? (
        <div className="text-[10px] text-muted-foreground mb-1 line-clamp-1">
          {description}
        </div>
      ) : null}
      <div className="flex-1 min-h-0">{children}</div>
    </div>
  );
}

function NoData(): JSX.Element {
  return (
    <div className="text-xs text-muted-foreground italic h-full flex items-center justify-center">
      (no data)
    </div>
  );
}
